'use client';
import { useEffect } from 'react';
import BlazeSlider from 'blaze-slider';
import 'blaze-slider/dist/blaze.css';
import Image from 'next/image';

export default function FeatureMediaText({ block, position }) {
  const {
    title,
    description_rte,
    slider_images,
    detail,
    stats,
    background_color,
    text_color
  } = block.feature_media_text;

  useEffect(() => {
    const desktopSliderEl = document.querySelector(
      `#feature-media-text-${position} .blaze-slider`
    );
    const mobileSliderEl = document.querySelector(
      `#feature-media-text-${position} .blaze-slider-mobile`
    );

    // Initialize desktop slider
    const desktopSlider = new BlazeSlider(desktopSliderEl, {
      all: {
        slidesToShow: 1,
        loop: true,
        transitionDuration: 400
      }
    });
    // Initialize mobile slider
    const mobileSlider = new BlazeSlider(mobileSliderEl, {
      all: {
        slidesToShow: 1,
        loop: true,
        transitionDuration: 400
      }
    });

    // Cleanup on unmount
    return () => {
      desktopSlider.destroy();
      mobileSlider.destroy();
    };
  }, [position]); // Only re-run if the position changes

  return (
    <section
      id={`feature-media-text-${position}`}
      data-name={'feature-media-text'}
      className={`feature-media-text large-padding overflow-hidden bg-white bg-[${background_color}] text-[${text_color}]`}
    >
      <div className="feature-container mx-auto">
        <div className="mx-auto grid grid-cols-1 gap-x-15 gap-y-16 sm:gap-y-20 lg:grid-cols-2">
          <div
            className={`blaze-slider my-auto hidden max-h-[570px] max-w-[570px] lg:block`}
          >
            <div className="blaze-container">
              <div className="blaze-track-container">
                <ul className="blaze-track" tabIndex={0}>
                  {slider_images.map((slide, index) => (
                    <li key={index} className="max-h-[570px] max-w-[570px]">
                      <Image
                        src={slide.url}
                        width="570"
                        height="570"
                        sizes="(min-width: 1536px) 570px, (min-width: 1280px) 450px, (min-width: 1024px) 355px, (min-width: 768px) 648px, (min-width: 640px) 380px"
                        alt={slide.title.replace(/\.[^/.]+$/, '')}
                        key={index}
                      />
                    </li>
                  ))}
                </ul>
              </div>
              {slider_images.length > 1 && (
                <>
                  <div className="blaze-prev-circle absolute top-[46%] left-5 mr-5 block hidden h-10 w-10 cursor-pointer rounded-full bg-white opacity-80 sm:block sm:min-w-[40px]">
                    <button
                      type="button"
                      className="blaze-prev blaze-prev-arrow block h-10 w-10"
                      aria-label="Go to previous slide"
                    ></button>
                  </div>
                  <div className="blaze-next-circle absolute top-[46%] right-0 mr-5 block hidden h-10 w-10 cursor-pointer rounded-full bg-white opacity-80 sm:block sm:min-w-[40px]">
                    <button
                      type="button"
                      className="blaze-next blaze-next-arrow block h-10 w-10"
                      aria-label="Go to previous slide"
                    ></button>
                  </div>
                </>
              )}
            </div>
            <div className="blaze-pagination indicators-dark mt-10 flex"></div>
          </div>

          <div className="lg:my-auto lg:pl-15">
            <div>
              <p className="h3 lg:h2 mb-4 text-center lg:text-left">{title}</p>
              <div
                className="body-l text-center lg:text-left"
                dangerouslySetInnerHTML={{ __html: description_rte }}
              />
              {detail?.length > 0 && (
                <div className="mt-15 space-y-10 lg:max-w-none">
                  {detail.map((item, index) => {
                    return (
                      <div key={index} className="relative flex">
                        <Image
                          src={item.icon.url}
                          width={48}
                          height={48}
                          sizes="48px"
                          alt={item.icon.title.replace(/\.[^/.]+$/, '')}
                          className="mr-4 hidden h-12 w-12 sm:block"
                          aria-hidden="true"
                        />
                        <div>
                          <div className="sh10 sm:sh9">{item.title}</div>
                          <div className="body-r">{item.description}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className={`blaze-slider blaze-slider-mobile block lg:hidden`}>
            <div className="blaze-container">
              <div className="blaze-track-container">
                <ul className="blaze-track" tabIndex={0}>
                  {slider_images.map((slide, index) => {
                    return (
                      <li key={index}>
                        <Image
                          src={`${slide.url}?format=pjpg&auto=webp&disable=upscale`}
                          width={slide.dimension.width}
                          height={slide.dimension.height}
                          alt={slide.title.replace(/\.[^/.]+$/, '')}
                          className="w-full"
                          key={index}
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
              {slider_images.length > 1 && (
                <>
                  <div className="blaze-prev-circle absolute top-[46%] left-5 mr-5 block hidden h-10 w-10 cursor-pointer rounded-full bg-white opacity-80 sm:block sm:min-w-[40px]">
                    <button
                      type="button"
                      className="blaze-prev blaze-prev-arrow block h-10 w-10"
                      aria-label="Go to previous slide"
                    ></button>
                  </div>
                  <div className="blaze-next-circle absolute top-[46%] right-0 mr-5 block hidden h-10 w-10 cursor-pointer rounded-full bg-white opacity-80 sm:block sm:min-w-[40px]">
                    <button
                      type="button"
                      className="blaze-next blaze-next-arrow block h-10 w-10"
                      aria-label="Go to previous slide"
                    ></button>
                  </div>
                </>
              )}
            </div>
            <div className="blaze-pagination indicators-dark mt-10 flex"></div>
          </div>
        </div>
        {stats.length > 0 && (
          <div className="relative mx-auto max-w-[1080px] justify-center">
            {stats.length == 3 ? (
              <div
                className={`body-r flex w-full flex-wrap pt-20 text-center lg:pt-[120px]`}
              >
                {stats.map((stat, index) => {
                  return (
                    <div
                      key={index}
                      className={`${index == 2 ? 'pt-10 sm:pt-0' : ''} ${index == 1 ? 'pl-5 sm:pl-0' : index == 0 ? 'pr-5 sm:pr-0' : ''} mx-auto inline-block max-w-1/2 sm:max-w-[210px]`}
                    >
                      <div className="stat-title mb-2">{stat.title}</div>
                      <div>{stat.description}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div
                className={`grid-cols grid grid-cols-2 gap-y-10 lg:grid-cols-${stats.length} body-r w-full gap-x-20 pt-20 text-center lg:pt-[120px]`}
              >
                {stats.map((stat, index) => {
                  return (
                    <div key={index} className="max-w-[210px] lg:mx-auto">
                      <div className="stat-title mb-2">{stat.title}</div>
                      <div>{stat.description}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
